import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import supabase from '../../utils/supabaseClient';
import LoadingScreen from '../../components/LoadingScreen';

const FinancialAnalysisPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productData, setProductData] = useState(null);
  const [financialData, setFinancialData] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch product data
        const { data: product, error: productError } = await supabase
          .from('products')
          .select('*')
          .eq('id', id)
          .single();
        
        if (productError) throw productError;
        setProductData(product);
        
        // Fetch financial input data
        const { data: financial, error: financialError } = await supabase
          .from('financial_data')
          .select('*')
          .eq('product_id', id)
          .maybeSingle();
        
        if (financialError) {
          console.error('Error fetching financial data:', financialError);
        } else if (financial) {
          setFinancialData(financial);
        }
        
        // Fetch financial analysis data
        const { data: analysis, error: analysisError } = await supabase
          .from('financial_analysis')
          .select('*')
          .eq('product_id', id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        
        if (analysisError) {
          // Check if the error is because the table doesn't exist
          if (analysisError.code === '42P01') {
            throw new Error('The financial_analysis table does not exist in your database.');
          }
          throw analysisError;
        }
        
        if (analysis) {
          setAnalysisData(analysis);
        } else {
          // No analysis found, redirect to financial input page
          setError('No financial analysis found. Please complete the financial analysis first.');
        }
        
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(`Failed to load financial analysis: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (user && id) {
      fetchData();
    }
  }, [user, id]);
  
  if (isLoading) {
    return <LoadingScreen message="Loading financial analysis..." />;
  }
  
  if (!productData) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6" role="alert">
          <p>Product not found. Please check the URL and try again.</p>
        </div>
        <Link to="/products" className="btn-primary">
          Back to Products
        </Link>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Link to={`/products/${id}`} className="text-primary-600 hover:text-primary-700 flex items-center">
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Back to Product Details
        </Link>
      </div>
      
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Financial Analysis Results</h1>
      
      {error ? (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6" role="alert">
          <p>{error}</p>
          {error.includes('financial_analysis table does not exist') && (
            <div className="mt-4">
              <p>To create the financial_analysis table, copy the SQL below and run it in your Supabase SQL Editor:</p>
              <div className="mt-2 p-4 bg-gray-800 text-white rounded overflow-auto max-h-96">
                <pre className="text-xs">
{`-- Create the financial_analysis table
CREATE TABLE IF NOT EXISTS public.financial_analysis (
    id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    product_id UUID NOT NULL REFERENCES public.products(id) ON DELETE CASCADE,
    user_id UUID NOT NULL,
    financial_data_id UUID REFERENCES public.financial_data(id),
    analysis_results JSONB NOT NULL,
    created_at TIMESTAMPTZ NOT NULL DEFAULT NOW(),
    updated_at TIMESTAMPTZ NOT NULL DEFAULT NOW()
);

-- Set up Row Level Security
ALTER TABLE public.financial_analysis ENABLE ROW LEVEL SECURITY;

-- Create policy to allow users to view their own financial analyses
CREATE POLICY "Users can view their own financial analyses"
ON public.financial_analysis
FOR SELECT
USING (auth.uid() = user_id);

-- Create policy to allow users to insert their own financial analyses
CREATE POLICY "Users can insert their own financial analyses"
ON public.financial_analysis
FOR INSERT
WITH CHECK (auth.uid() = user_id);

-- Create policy to allow users to update their own financial analyses
CREATE POLICY "Users can update their own financial analyses"
ON public.financial_analysis
FOR UPDATE
USING (auth.uid() = user_id);

-- Add indexes for better performance
CREATE INDEX IF NOT EXISTS financial_analysis_product_id_idx ON public.financial_analysis (product_id);
CREATE INDEX IF NOT EXISTS financial_analysis_user_id_idx ON public.financial_analysis (user_id);
CREATE INDEX IF NOT EXISTS financial_analysis_financial_data_id_idx ON public.financial_analysis (financial_data_id);`}
                </pre>
              </div>
            </div>
          )}
          <div className="mt-4">
            <button
              onClick={() => navigate(`/products/${id}/financial`)}
              className="btn-primary"
            >
              Go to Financial Input
            </button>
          </div>
        </div>
      ) : analysisData ? (
        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-semibold text-gray-800 mb-1">{productData.name}</h2>
              <p className="text-gray-600">{productData.development_stage} Stage</p>
            </div>
            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
              Analysis Complete
            </span>
          </div>
          
          {/* Financial Parameters Summary */}
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-800 mb-3">Financial Parameters</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 bg-gray-50 p-4 rounded-md">
              <div>
                <p className="text-sm text-gray-500">Project Risk</p>
                <p className="font-medium">{financialData?.overall_project_risk || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Discount Rate</p>
                <p className="font-medium">{financialData?.discount_rate || 'N/A'}%</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Exclusivity Period</p>
                <p className="font-medium">{financialData?.duration_of_exclusivity || 'N/A'} years</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Gross Margin</p>
                <p className="font-medium">{financialData?.gross_profit_margin || 'N/A'}%</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">R&D to POC</p>
                <p className="font-medium">${(financialData?.rd_costs_to_preclinical_poc || 0).toLocaleString()}</p>
              </div>
            </div>
          </div>
          
          {/* Key Financial Metrics */}
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-800 mb-3">Key Financial Metrics</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-blue-50 p-4 rounded-md">
                <p className="text-sm text-blue-700 mb-1">Net Present Value (NPV)</p>
                <p className="text-2xl font-bold text-blue-900">
                  {analysisData.analysis_results["Financial Analysis"]?.NPV || analysisData.analysis_results.npv || 'N/A'}
                </p>
              </div>
              
              <div className="bg-green-50 p-4 rounded-md">
                <p className="text-sm text-green-700 mb-1">Internal Rate of Return (IRR)</p>
                <p className="text-2xl font-bold text-green-900">
                  {analysisData.analysis_results["Financial Analysis"]?.IRR || analysisData.analysis_results.irr || 'N/A'}
                </p>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-md">
                <p className="text-sm text-purple-700 mb-1">Break-even Timeline</p>
                <p className="text-2xl font-bold text-purple-900">
                  {analysisData.analysis_results["Financial Analysis"]?.["Break-even Analysis"]?.["Break-even Year"] || analysisData.analysis_results.breakEvenPoint || 'N/A'}
                </p>
              </div>
            </div>
          </div>
          
          {/* Revenue Projections */}
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-800 mb-3">Revenue Projections</h3>
            {analysisData.analysis_results["Financial Analysis"]?.["Year-by-year Revenue Projections"] || analysisData.analysis_results.revenueProjections ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Year</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Growth</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Object.entries(analysisData.analysis_results["Financial Analysis"]?.["Year-by-year Revenue Projections"] || analysisData.analysis_results.revenueProjections || {}).map(([year, data], index) => {
                      // Handle both data structures
                      const revenue = typeof data === 'object' ? data.revenue : data;
                      const growth = typeof data === 'object' ? data.growth : 'N/A';
                      
                      return (
                        <tr key={year} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{year}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{revenue}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {growth !== 'N/A' ? (
                              <span className={growth.includes('-') ? 'text-red-600' : 'text-green-600'}>
                                {growth}
                              </span>
                            ) : 'N/A'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 italic">Revenue projection data not available</p>
            )}
          </div>
          
          {/* Sensitivity Analysis */}
          {analysisData.analysis_results["Financial Analysis"]?.["Sensitivity Analysis"] || analysisData.analysis_results.sensitivityAnalysis ? (
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-800 mb-3">Sensitivity Analysis</h3>
              <div className="bg-gray-50 p-4 rounded-md">
                <pre className="whitespace-pre-wrap text-sm text-gray-700">
                  {typeof (analysisData.analysis_results["Financial Analysis"]?.["Sensitivity Analysis"]) === 'object' 
                    ? JSON.stringify(analysisData.analysis_results["Financial Analysis"]?.["Sensitivity Analysis"], null, 2)
                    : (analysisData.analysis_results["Financial Analysis"]?.["Sensitivity Analysis"] || analysisData.analysis_results.sensitivityAnalysis)}
                </pre>
              </div>
            </div>
          ) : null}
          
          {/* Financial Recommendations */}
          {analysisData.analysis_results["Financial Analysis"]?.["Financial Recommendations"] || analysisData.analysis_results.recommendations ? (
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-800 mb-3">Recommendations</h3>
              <div className="bg-yellow-50 p-4 rounded-md">
                <p className="text-gray-700">
                  {analysisData.analysis_results["Financial Analysis"]?.["Financial Recommendations"] || analysisData.analysis_results.recommendations}
                </p>
              </div>
            </div>
          ) : null}
          
          {/* Key Financial Metrics Details */}
          {analysisData.analysis_results["Financial Analysis"]?.["Key Financial Metrics"] || analysisData.analysis_results.keyMetrics ? (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-3">Additional Financial Metrics</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(analysisData.analysis_results["Financial Analysis"]?.["Key Financial Metrics"] || analysisData.analysis_results.keyMetrics || {}).map(([key, value]) => (
                  <div key={key} className="bg-gray-50 p-3 rounded-md">
                    <p className="text-sm text-gray-500">{key}</p>
                    <p className="font-medium">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          
          <div className="flex justify-end space-x-4 mt-8">
            <Link
              to={`/products/${id}/financial`}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Edit Financial Parameters
            </Link>
            
            <Link
              to={`/products/${id}/pitchdeck`}
              className="btn-primary"
            >
              Continue to Pitch Deck
            </Link>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg p-6 mb-6 text-center">
          <div className="mb-4">
            <svg className="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
          </div>
          <h2 className="text-xl font-medium text-gray-900 mb-2">No Financial Analysis Available</h2>
          <p className="text-gray-600 mb-6">
            You haven't completed a financial analysis for this product yet. 
            Complete the financial input form and run the analysis to see detailed financial projections.
          </p>
          <button
            onClick={() => navigate(`/products/${id}/financial`)}
            className="btn-primary"
          >
            Go to Financial Input
          </button>
        </div>
      )}
    </div>
  );
};

export default FinancialAnalysisPage; 